import { useEffect, useRef } from 'react';
import { MapBounds } from '../types';
import { reverseGeocodeRegion } from '../services/regionEnrichmentService';
import { logRegionView } from '../services/firebaseService';

const DEBOUNCE_MS = 2000;

/**
 * Tracks which county the user is viewing on the map.
 * Skips the initial default position — only logs after the user has
 * actually panned or zoomed (i.e. bounds have changed at least once).
 * Debounced, throttled, and fully aggregated (no user-level data stored).
 */
export const useRegionViewTracking = (
  currentViewBounds: MapBounds | null,
  isLoggedIn: boolean
) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCenterRef = useRef<string | null>(null);
  const changeCountRef = useRef(0);

  useEffect(() => {
    if (!isLoggedIn || !currentViewBounds) return;

    const centerLat = (currentViewBounds.latMin + currentViewBounds.latMax) / 2;
    const centerLon = (currentViewBounds.lonMin + currentViewBounds.lonMax) / 2;

    // Coarse rounding (~1km) to avoid redundant API calls for small pans
    const coarseKey = `${centerLat.toFixed(2)},${centerLon.toFixed(2)}`;
    if (coarseKey === lastCenterRef.current) return;
    lastCenterRef.current = coarseKey;

    // Skip the first position change (default landing area)
    changeCountRef.current++;
    if (changeCountRef.current <= 1) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const region = await reverseGeocodeRegion({
          latitude: centerLat,
          longitude: centerLon,
        });
        if (region?.county) {
          logRegionView(region.county);
        }
      } catch {
        // Silently ignore
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentViewBounds, isLoggedIn]);
};
